import React from 'react';
import { CTAButton, ErrorNotification, Modal, P } from '@ovotech/element-v4';
import { AnalyticsClick, useAccountContext } from '@ovotech/ui-tools';
import { HOME_MOVES_CANCEL_MOVE_MODAL_CONFIRM } from '@/src/constants/analytics-subjects';
import styles from './CancelMoveModal.module.css';
import { LoadingContent } from '@ovotech/energy-cx/dist/shared/core/components';
import { useCancelMoveOutMutation } from '@/src/api/home-moves';

type CancelMoveModalProps = {
  onClose: () => void;
};

/**
 * Modal component for canceling a home move using the new Home Moves API
 */
export const CancelMoveModal = ({ onClose }: CancelMoveModalProps) => {
  const { accountId } = useAccountContext();
  const cancelMoveOutMutation = useCancelMoveOutMutation();

  const handleCancel = async () => {
    if (!accountId) return;

    try {
      await cancelMoveOutMutation.mutateAsync({ accountId });
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error('Failed to cancel move out:', error);
    }
  };

  return (
    <Modal
      title="Cancel your move"
      isOpen={true}
      onClose={onClose}
      isDismissable={!cancelMoveOutMutation.isLoading}
    >
      <div>
        {cancelMoveOutMutation.isError ? (
          <ErrorNotification
            title="Sorry, there's a bit of a problem"
            id={'move-out-api-error'}
          >
            <P>
              We&apos;re working hard to fix it. But in the meantime, please try
              refreshing the page.
            </P>
          </ErrorNotification>
        ) : cancelMoveOutMutation.isSuccess ? (
          <>
            <P>
              We've cancelled your move out, and won't cancel your contract.
            </P>
            <P>This may take a few minutes to update on the website.</P>
          </>
        ) : (
          <div>
            You're about to cancel your home move. You'll need to start over if
            you want to tell us about an upcoming or recent move.
          </div>
        )}

        <div className={styles.buttonsGroup}>
          {!cancelMoveOutMutation.isSuccess ? (
            <AnalyticsClick name={HOME_MOVES_CANCEL_MOVE_MODAL_CONFIRM}>
              <CTAButton onClick={handleCancel}>
                <LoadingContent loading={cancelMoveOutMutation.isLoading}>
                  Cancel my home move
                </LoadingContent>
              </CTAButton>
            </AnalyticsClick>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
